import React, { useEffect, useState } from "react";
import { format } from "date-fns";
import { pt } from "date-fns/locale";

// config
import { MINIMUM_VIDEO_PLAYED } from "~/services/conf";
import { BLOCK_BY_VIDEO_PAYED } from "~/services/conf";

// internal components
import Video from "./Video";
import ButtonStatus from "./ButtonStatus";

// style
import {
  Container,
  LabelFinishedCourse,
  WrapButtonFinishedClass,
  WrapTextDesc,
  WrapVideo,
} from "./style";

// component
import { LoadingCourseContent } from "~/components/Loading";
import NextClassOrTest from "~/components/NextClassOrTest";

// rest
import { useGet, usePut } from "~/services/rest";

// functions
import { stripSlashes } from "~/services/functions";

const Content = ({
  idHistoricClass,
  handleTestContent,
  handleClassContent,
  setUpdateCourseSidebar,
  setMinPercentagePlayed,
}) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  });

  const [dataCarregamento] = useState(() => new Date().toISOString());
  const classContent = useGet(`historicoaulas/${idHistoricClass}`);
  const courseContent = useGet(
    classContent.data[0]
      ? `historicocursos/${classContent.data[0].historico_modulos.historico_cursos_id}`
      : false,
  );

  const [lessonUpdatedWarning, setLessonUpdatedWarning] = useState(false);

  const [putClassRes, updateHistoricCLass] = usePut(
    classContent.data.length > 0 &&
      `historicoaulas/${classContent.data[0].idhistorico_aulas}`,
  );

  useEffect(() => {
    if (putClassRes.data && putClassRes.data.aula_atualizada) {
      setLessonUpdatedWarning(true); // Só isso! Deixa a tela paradinha.
    }
  }, [putClassRes.data]);

  // id da aula
  const idAula = classContent.data[0]?.aulas?.idaulas;

  const listTests = useGet(
    idAula && idHistoricClass
      ? `testes/aula/${idAula}?id_historico=${idHistoricClass}&tipo=video`
      : false,
  );

  const videoTests = listTests.data || [];
  const [needToTest, setNeedToTest] = useState(true);
  const [videoWatched, setVideoWatched] = useState(null);
  const [percentagePlayed, setPercentagePlayed] = useState(0);
  const [lastPercentagePlayed, setLastPercentagePlayed] = useState(0);

  // zera contador de porcentagem sempre que carrega tela
  useEffect(() => {
    setPercentagePlayed(classContent.data[0]?.video_assistido_porcentagem);
    setLastPercentagePlayed(classContent.data[0]?.video_assistido_porcentagem);
    setMinPercentagePlayed(false);
  }, [classContent.data, setMinPercentagePlayed]);

  useEffect(() => {
    if (classContent && classContent.data && classContent.data.length > 0) {
      const dadosAula = classContent.data[0];

      if (dadosAula.teste_resultado && dadosAula.teste_resultado.length > 0) {
        for (let i = 0; i < dadosAula.teste_resultado.length; i++) {
          const resultado = dadosAula.teste_resultado[i];

          const tipoTeste = resultado.testes?.tipo;

          const isAprovado = resultado.aprovado === 1;

          // aprovado E NÃO é teste de vídeo
          if (isAprovado && tipoTeste !== "video") {
            setNeedToTest(false);
            break;
          }
        }
      }
    }
  }, [classContent.data]);

  // pega dados se usuario ja assistiu o mínimo desse video
  useEffect(() => {
    if (classContent.data.length > 0) {
      setVideoWatched(classContent.data[0].video_assistido === 1);
      setMinPercentagePlayed(classContent.data[0].video_assistido === 1); // estado da página principal (Course) para controle da RightSidebar
    }
  }, [classContent.data, setMinPercentagePlayed]);

  // verifica em tempo real do player se o usuário ja assitiu o tanto mínimo do vídeo
  useEffect(() => {
    if (percentagePlayed >= MINIMUM_VIDEO_PLAYED) {
      if (!videoWatched) {
        updateHistoricCLass({ video_assistido: true });
        setVideoWatched(true);
        setMinPercentagePlayed(true); // estado da página principal (Course) para controle da RightSidebar
        setUpdateCourseSidebar(true);
      }
    }
  }, [
    percentagePlayed,
    updateHistoricCLass,
    videoWatched,
    setVideoWatched,
    setUpdateCourseSidebar,
    setMinPercentagePlayed,
  ]);

  useEffect(() => {
    if (!classContent.data || classContent.data.length === 0) return;

    const progressoIntervalo = percentagePlayed > lastPercentagePlayed;
    const cruzouMeta80 = lastPercentagePlayed < 80 && percentagePlayed >= 80;
    const concluiu = percentagePlayed >= 100;

    if (progressoIntervalo || cruzouMeta80 || concluiu) {
      const dadosAula = classContent.data[0];
      const payloadUpdate = {
        video_assistido_porcentagem: percentagePlayed,
        dados_carregados_em: dataCarregamento,
      };

      const naoTemTeste = !dadosAula.aulas.habilita_teste;
      const aulaNaoEstaConcluida = dadosAula.status !== 11;

      // se cruzou 80%, NÃO tem teste e a aula ainda não foi finalizada:
      if (cruzouMeta80 && naoTemTeste && aulaNaoEstaConcluida) {
        payloadUpdate.status = 11;
        payloadUpdate.concluido_em = format(
          new Date(),
          "yyyy-MM-dd'T'HH:mm:ss",
        );

        dadosAula.status = 11;
        dadosAula.concluido_em = payloadUpdate.concluido_em;
      }
      updateHistoricCLass(payloadUpdate);
      setLastPercentagePlayed(percentagePlayed);

      if (cruzouMeta80) {
        setUpdateCourseSidebar(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [percentagePlayed]);

  // loading component
  if (classContent.data.length === 0 || classContent.loading) {
    return <LoadingCourseContent />;
  }

  // error component
  if (classContent.error) {
    return (
      <Container>
        <h2>Ops, algo deu errado!</h2>
        <p>
          <strong> {classContent.error} </strong>
        </p>
        <p>Por gentileza, tente novamente</p>
      </Container>
    );
  }

  return (
    <Container>
      {classContent.data[0].aulas.video_id && (
        <WrapVideo>
          <Video
            idVideo={classContent.data[0].aulas.video_id}
            videoWhatched={videoWatched}
            setPercentagePlayed={setPercentagePlayed}
            historicClass={classContent.data[0]}
            setUpdateCourseSidebar={setUpdateCourseSidebar}
            videoTests={videoTests}
            lessonUpdatedWarning={lessonUpdatedWarning}
            setLessonUpdatedWarning={setLessonUpdatedWarning}
          />
          <div className="wrap-infos-video">
            <p className="info-percentage">
              Para concluir esta aula você deverá assistir o mínimo de 80% de
              uma só vez
            </p>
            <p
              className={`percentage-watched ${percentagePlayed >= 80 && "finished"}`}
            >
              {percentagePlayed}%
            </p>
          </div>
        </WrapVideo>
      )}

      {videoWatched ? (
        classContent.data[0].aulas.habilita_teste ? ( // verifica se aula possui teste
          !needToTest && ( // se usuário não precisa fazer o teste então renderiza o botão
            <WrapButtonFinishedClass>
              <ButtonStatus
                classContent={classContent.data[0]}
                setUpdateCourseSidebar={setUpdateCourseSidebar}
              />
              {classContent.data[0].concluido_em && (
                <p>
                  Aula concluída em: <br />
                  <span>
                    {format(
                      new Date(classContent.data[0].concluido_em),
                      "dd/MM/yy",
                      { locale: pt },
                    )}
                  </span>
                  às
                  <span>
                    {format(
                      new Date(classContent.data[0].concluido_em),
                      "HH:mm",
                      { locale: pt },
                    )}
                  </span>
                </p>
              )}
            </WrapButtonFinishedClass>
          )
        ) : (
          <WrapButtonFinishedClass>
            <ButtonStatus // se não tiver teste também renderiza o botão
              classContent={classContent.data[0]}
              setUpdateCourseSidebar={setUpdateCourseSidebar}
            />
            {classContent.data[0].concluido_em && (
              <p>
                Aula concluída em: <br />
                <span>
                  {format(
                    new Date(classContent.data[0].concluido_em),
                    "dd/MM/yy",
                    { locale: pt },
                  )}
                </span>
                às
                <span>
                  {format(
                    new Date(classContent.data[0].concluido_em),
                    "HH:mm",
                    { locale: pt },
                  )}
                </span>
              </p>
            )}
          </WrapButtonFinishedClass>
        )
      ) : (
        <></>
      )}

      <h2 className="title">
        {stripSlashes(classContent.data[0].aulas.titulo)}
      </h2>

      {classContent.data[0].aulas.descricao && (
        <WrapTextDesc
          dangerouslySetInnerHTML={{
            __html: stripSlashes(classContent.data[0].aulas.descricao),
          }}
        />
      )}

      {BLOCK_BY_VIDEO_PAYED ? (
        videoWatched && (
          <NextClassOrTest
            classData={classContent.data[0]}
            handleTestContent={handleTestContent}
            handleClassContent={handleClassContent}
            setUpdateCourseSidebar={setUpdateCourseSidebar}
          />
        )
      ) : (
        <NextClassOrTest
          classData={classContent.data[0]}
          handleTestContent={handleTestContent}
          handleClassContent={handleClassContent}
          setUpdateCourseSidebar={setUpdateCourseSidebar}
        />
      )}

      {courseContent.data[0] &&
        courseContent.data[0].concluido_em &&
        courseContent.data[0].status === 11 &&
        courseContent.data[0].cursos.certificado_automatico === 1 && (
          <LabelFinishedCourse>
            Curso concluído em:
            <span>
              {format(new Date(courseContent.data[0].concluido_em), "dd", {
                locale: pt,
              })}
            </span>
            de
            <span>
              {format(new Date(courseContent.data[0].concluido_em), "MMMM", {
                locale: pt,
              })}
            </span>
            às
            <span>
              {format(new Date(courseContent.data[0].concluido_em), "HH:mm", {
                locale: pt,
              })}
            </span>
          </LabelFinishedCourse>
        )}
    </Container>
  );
};

export default Content;
